import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Checkbox } from "./components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import RDOFormNew from "./components/RDOFormNew";
import ParametersPanel from "./components/ParametersPanel";
import DraftsList from "./components/DraftsList";
import CustomQuestionsManager from "./components/CustomQuestionsManager";
import StudyDashboard from "./components/StudyDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuração do axios
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Context para autenticação
const AuthContext = React.createContext();

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      setToken(null);
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success("Login realizado com sucesso!");
      return true;
    } catch (error) {
      toast.error("Credenciais inválidas");
      return false;
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const { access_token, user: newUser } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(newUser);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success("Cadastro realizado com sucesso!");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cadastrar usuário");
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    toast.success("Logout realizado com sucesso!");
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Componente de Login/Registro
const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    full_name: '',
    is_admin: false
  });
  const { login, register } = useAuth();

  // Consulta o backend para saber se ainda estamos no modo bootstrap (sem nenhum admin)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/needs-bootstrap`);
        if (!cancelled) setNeedsBootstrap(!!res.data?.needs_bootstrap);
      } catch {
        if (!cancelled) setNeedsBootstrap(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLogin) {
      await login(formData.username, formData.password);
    } else {
      // Garantia client-side: nunca enviar is_admin=true se já saiu do bootstrap
      const payload = needsBootstrap ? formData : { ...formData, is_admin: false };
      await register(payload);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-slate-800">
            RDO - Sistema de Relatórios
          </CardTitle>
          <p className="text-slate-600">
            {isLogin ? "Faça login para continuar" : "Crie sua conta"}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username">Nome de usuário</Label>
              <Input
                id="username"
                data-testid="username-input"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                required
              />
            </div>
            
            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="full_name">Nome completo</Label>
                  <Input
                    id="full_name"
                    data-testid="fullname-input"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
              </>
            )}
            
            <div>
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                data-testid="password-input"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
              />
            </div>

            {!isLogin && needsBootstrap && (
              <div className="flex items-center space-x-2 p-3 rounded-md bg-amber-50 border border-amber-200">
                <Checkbox
                  id="is_admin"
                  data-testid="admin-checkbox"
                  checked={formData.is_admin}
                  onCheckedChange={(checked) => setFormData({...formData, is_admin: checked})}
                />
                <Label htmlFor="is_admin" className="text-sm">
                  Registrar como Administrador
                  <span className="block text-xs text-amber-700 font-normal">
                    Modo inicial: nenhum administrador cadastrado ainda. Marque esta opção para se tornar o primeiro admin do sistema.
                  </span>
                </Label>
              </div>
            )}

            <Button type="submit" className="w-full" data-testid="auth-submit-btn">
              {isLogin ? "Entrar" : "Cadastrar"}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Button 
              variant="link" 
              onClick={() => setIsLogin(!isLogin)}
              data-testid="toggle-auth-btn"
            >
              {isLogin ? "Não tem conta? Cadastre-se" : "Já tem conta? Faça login"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Componente principal do dashboard
const Dashboard = () => {
  const { user, logout } = useAuth();
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const isViewer = user?.role === "viewer";
  const isAdmin = !!user?.is_admin;
  const canSeeDashboard = isAdmin || !!user?.can_access_dashboard;
  // Viewer só vê o Dashboard; demais começam em "novo"
  const [activeTab, setActiveTab] = useState(isViewer ? 'dashboard' : 'novo');
  const [resumingDraft, setResumingDraft] = useState(null);  // Rascunho retomado em "Novo Relatório"
  const [draftsRefreshKey, setDraftsRefreshKey] = useState(0);

  useEffect(() => {
    if (!isViewer) fetchReports();
  }, [isViewer]);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports`);
      setReports(response.data);
    } catch (error) {
      toast.error("Erro ao carregar relatórios");
    }
  };

  const downloadManual = async () => {
    try {
      const response = await axios.get(`${API}/download/manual`, {
        responseType: 'blob'
      });
      
      // Criar link de download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Manual_RDO.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Manual baixado com sucesso!");
    } catch (error) {
      toast.error("Erro ao baixar manual");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-semibold text-slate-900">
              RDO - Relatórios Diários de Operações
            </h1>
            <div className="flex items-center gap-4">
              <Badge variant="secondary" data-testid="user-badge">
                {user?.full_name} {user?.is_admin ? "(Admin)" : user?.role === "viewer" ? "(Cliente)" : ""}
              </Badge>
              <Button 
                variant="outline" 
                onClick={downloadManual}
                data-testid="download-manual-btn"
              >
                📄 Manual PDF
              </Button>
              <Button 
                variant="outline" 
                onClick={logout}
                data-testid="logout-btn"
              >
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isViewer && !canSeeDashboard && (
          <div className="max-w-2xl mx-auto py-12 text-center" data-testid="viewer-no-access">
            <h3 className="text-lg font-semibold mb-2">🔒 Acesso ao Dashboard pendente</h3>
            <p className="text-sm text-slate-600">
              Sua conta de cliente foi criada, mas o administrador ainda não liberou o acesso ao Dashboard.
              <br />Entre em contato com o suporte LocService.
            </p>
          </div>
        )}
        <Tabs value={activeTab} onValueChange={(v) => {
          // Ao sair da aba "novo", limpar resumingDraft para próxima visita iniciar limpa
          if (v !== 'novo' && resumingDraft) setResumingDraft(null);
          setActiveTab(v);
        }}>
          {(() => {
            // Calcula abas visíveis dinamicamente conforme role/permissões
            const tabs = [];
            if (!isViewer) {
              tabs.push({ value: 'novo', testid: 'new-report-tab', label: 'Novo Relatório', short: 'Novo' });
              tabs.push({ value: 'rascunhos', testid: 'drafts-tab', label: '📝 Rascunhos', short: '📝' });
              tabs.push({ value: 'meus', testid: 'my-reports-tab', label: 'Meus Relatórios', short: 'Meus' });
            }
            if (canSeeDashboard) {
              tabs.push({ value: 'dashboard', testid: 'dashboard-tab', label: 'Dashboard', short: 'Dashboard' });
            }
            if (isAdmin) {
              tabs.push({ value: 'todos', testid: 'all-reports-tab', label: 'Todos os Relatórios', short: 'Todos' });
              tabs.push({ value: 'admin', testid: 'admin-panel-tab', label: '⚙️ Painel Admin', short: '⚙️ Admin' });
              tabs.push({ value: 'parameters', testid: 'parameters-tab', label: '📊 Parâmetros', short: '📊 Params' });
            }
            const colsCls = `grid w-full gap-1 ${tabs.length >= 6 ? 'grid-cols-3 md:grid-cols-7' : tabs.length >= 4 ? 'grid-cols-4' : `grid-cols-${tabs.length}`}`;
            return (
              <TabsList className={colsCls}>
                {tabs.map(t => (
                  <TabsTrigger key={t.value} value={t.value} data-testid={t.testid} className="text-xs sm:text-sm">
                    <span className="hidden sm:inline">{t.label}</span>
                    <span className="sm:hidden">{t.short}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            );
          })()}

          {!isViewer && (
            <TabsContent value="novo" className="mt-6">
              <RDOFormNew
                key={resumingDraft?.id || 'new-fresh'}
                editingReport={resumingDraft}
                onReportCreated={(action) => {
                  setResumingDraft(null);
                  setDraftsRefreshKey(k => k + 1);
                  if (action === 'navigate-to-reports') {
                    setActiveTab('meus');
                  } else {
                    fetchReports();
                  }
                }}
              />
            </TabsContent>
          )}

          {!isViewer && (
            <TabsContent value="rascunhos" className="mt-6">
              <DraftsList
                refreshKey={draftsRefreshKey}
                onResume={(d) => {
                  setResumingDraft(d);
                  setActiveTab('novo');
                }}
              />
            </TabsContent>
          )}

          {!isViewer && (
            <TabsContent value="meus" className="mt-6">
              <ReportsList 
                reports={reports.filter(r => r.user_id === user?.id)} 
                onReportUpdated={fetchReports}
                showEdit={true}
              />
            </TabsContent>
          )}

          {canSeeDashboard && (
            <TabsContent value="dashboard" className="mt-6">
              <StudyDashboard />
            </TabsContent>
          )}

          {user?.is_admin && (
            <TabsContent value="todos" className="mt-6">
              <ReportsList 
                reports={reports} 
                onReportUpdated={fetchReports}
                showEdit={false}
              />
            </TabsContent>
          )}

          {user?.is_admin && (
            <TabsContent value="admin" className="mt-6">
              <AdminPanel />
            </TabsContent>
          )}

          {user?.is_admin && (
            <TabsContent value="parameters" className="mt-6">
              <ParametersPanel />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
};

// Componente do formulário RDO
const RDOForm = ({ onReportCreated, editingReport = null }) => {
  const [formData, setFormData] = useState({
    report_date: new Date().toISOString().split('T')[0],
    pt_room_time: '',
    pt_issuance_time: '',
    pt_numbers: '',
    pt_approval_time: '',
    morning_start_time: '',
    morning_activities: [],
    morning_comments: '',
    afternoon_activities: [],
    afternoon_comments: '',
    impediments_occurred: null,
    impediments_list: [],
    impediments_comments: '',
    impediment_resolution_time: '',
    region_worked: '',
    columns_sandblasted_entirely: '',
    sandblasted_columns_width: '',
    sandblasted_columns_height: '',
    partial_columns_sandblasted: null,
    partial_sandblasted_height: '',
    sandblasting_hours: '',
    sandblasting_rework: null,
    sandblasting_rework_details: '',
    robot_advance_speed: '',
    turbine_speed: '',
    turbine_speed_change_cav: '',
    sandblasting_current: '',
    implement_change: null,
    tool_change_time: '',
    columns_painted_entirely: '',
    painted_columns_width: '',
    painted_columns_height: '',
    partial_columns_painted: null,
    partial_painted_width: '',
    partial_painted_height: '',
    painting_hours: '',
    warehouse_item_moved: null,
    urgent_replacement_needed: null,
    urgent_replacement_comments: '',
    turbine_kit_replaced: null,
    turbine_kit_balanced: null,
    sealing_kit_replaced: null,
    sealing_kit_balanced: null,
    sealing_kits_stock: '',
    grit_used_volume: '',
    abrasive_loss: null,
    grit_remaining_volume: '',
    grit_replenishment_triggered: null,
    grit_stock_comments: '',
    paint_used_volume: '',
    paint_remaining_volume: '',
    component_ab_used_volume: '',
    component_ab_remaining_volume: '',
    inhibitor_used_volume: '',
    inhibitor_remaining_volume: '',
    solvent_used_volume: '',
    solvent_remaining_volume: '',
    daily_end_time: '',
    administrative_demands: ''
  });

  // Populate form if editing
  useEffect(() => {
    if (editingReport) {
      setFormData(editingReport);
    }
  }, [editingReport]);

  const activityOptions = [
    { value: "RDO_ISSUANCE", label: "Emissão do Diário de Bordo - Relatório RDO" },
    { value: "CORRECTIVE_MAINTENANCE", label: "Manutenção Corretiva" },
    { value: "PREVENTIVE_MAINTENANCE", label: "Manutenção Preventiva" },
    { value: "CONTAINER_ORG_CLEANING", label: "Organização e Limpeza dos Containers" },
    { value: "WAREHOUSE_COUNT", label: "Contagem do Almoxarifado no Container" },
    { value: "SANDBLASTING", label: "Jateamento" },
    { value: "SANDBLASTING_REWORK", label: "Repassagem de Jateamento" },
    { value: "ROUGHNESS_MEASUREMENT", label: "Medição de Rugosidade" },
    { value: "CORROSION_INHIBITOR_APP", label: "Aplicação de Inibidor de Corrozão" },
    { value: "PAINTING_LAYER1", label: "Pintura - Camada 01" },
    { value: "PAINTING_LAYER2", label: "Pintura - Camada 02" },
    { value: "PAINTING_LAYER3", label: "Pintura - Camada 03" },
    { value: "THICKNESS_MEASUREMENT", label: "Medição de Espessura" },
    { value: "OTHER", label: "Outras" }
  ];

  const impedimentOptions = [
    { value: "PT_ISSUANCE", label: "Emissão de PT" },
    { value: "PT_APPROVAL", label: "Aprovação PT" },
    { value: "GANGWAY", label: "Gangway" },
    { value: "LOAD_MOVEMENT", label: "Movimentação de Carga" },
    { value: "SWELL_INCREASE", label: "Aumento nas Ondulações (Swell)" },
    { value: "TOOL_CHANGE_BLAST_PAINT", label: "Troca de implemento de Jato para pintura (1:30hrs)" },
    { value: "SCAFFOLDING_TEAM", label: "Atendimento da equipe de andâimes" },
    { value: "WEATHER_CONDITIONS", label: "Condições do tempo (Chuva/ vento)" },
    { value: "MAINTENANCE", label: "Manutenções" },
    { value: "OTHER", label: "Outras" }
  ];

  const handleActivityChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Convert string numbers to numbers
      const processedData = { ...formData };
      const numberFields = [
        'columns_sandblasted_entirely', 'sandblasted_columns_width', 'sandblasted_columns_height',
        'partial_sandblasted_height', 'sandblasting_hours', 'robot_advance_speed', 'turbine_speed',
        'sandblasting_current', 'columns_painted_entirely', 'painted_columns_width', 'painted_columns_height',
        'partial_painted_width', 'partial_painted_height', 'painting_hours', 'sealing_kits_stock',
        'grit_used_volume', 'grit_remaining_volume', 'paint_used_volume', 'paint_remaining_volume',
        'component_ab_used_volume', 'component_ab_remaining_volume', 'inhibitor_used_volume',
        'inhibitor_remaining_volume', 'solvent_used_volume', 'solvent_remaining_volume'
      ];

      numberFields.forEach(field => {
        if (processedData[field] === '' || processedData[field] === null) {
          processedData[field] = null;
        } else {
          processedData[field] = parseFloat(processedData[field]) || null;
        }
      });

      if (editingReport) {
        await axios.put(`${API}/reports/${editingReport.id}`, processedData);
        toast.success("Relatório atualizado com sucesso!");
      } else {
        await axios.post(`${API}/reports`, processedData);
        toast.success("Relatório criado com sucesso!");
      }
      
      onReportCreated();
      
      if (!editingReport) {
        // Reset form for new report
        setFormData({
          ...formData,
          report_date: new Date().toISOString().split('T')[0]
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar relatório");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {editingReport ? "Editar Relatório RDO" : "Novo Relatório RDO"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Seção 1: Informações da Parada de Almoço */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">
              1. Informações sobre a Parada de Almoço
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="report_date">Data do Relatório</Label>
                <Input
                  id="report_date"
                  type="date"
                  data-testid="report-date-input"
                  value={formData.report_date}
                  onChange={(e) => setFormData({...formData, report_date: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="pt_room_time">Horário na sala de PT</Label>
                <Input
                  id="pt_room_time"
                  type="time"
                  data-testid="pt-room-time-input"
                  value={formData.pt_room_time}
                  onChange={(e) => setFormData({...formData, pt_room_time: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="pt_issuance_time">Horário de emissão da PT</Label>
                <Input
                  id="pt_issuance_time"
                  type="time"
                  data-testid="pt-issuance-time-input"
                  value={formData.pt_issuance_time}
                  onChange={(e) => setFormData({...formData, pt_issuance_time: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="pt_numbers">Número das PTs</Label>
                <Input
                  id="pt_numbers"
                  data-testid="pt-numbers-input"
                  placeholder="Ex: PT123, PT456"
                  value={formData.pt_numbers}
                  onChange={(e) => setFormData({...formData, pt_numbers: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="pt_approval_time">Horário de aprovação da PT</Label>
                <Input
                  id="pt_approval_time"
                  type="time"
                  data-testid="pt-approval-time-input"
                  value={formData.pt_approval_time}
                  onChange={(e) => setFormData({...formData, pt_approval_time: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="morning_start_time">Horário de início das atividades da manhã</Label>
                <Input
                  id="morning_start_time"
                  type="time"
                  data-testid="morning-start-time-input"
                  value={formData.morning_start_time}
                  onChange={(e) => setFormData({...formData, morning_start_time: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Seção 2: Atividades da Manhã */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">
              2. Atividades da Manhã
            </h3>
            
            <div>
              <Label>Indique as atividades da MANHÃ</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                {activityOptions.map(option => (
                  <div key={`morning_${option.value}`} className="flex items-center space-x-2">
                    <Checkbox
                      id={`morning_${option.value}`}
                      data-testid={`morning-activity-${option.value}`}
                      checked={formData.morning_activities.includes(option.value)}
                      onCheckedChange={(checked) => handleActivityChange('morning_activities', option.value, checked)}
                    />
                    <Label htmlFor={`morning_${option.value}`} className="text-sm">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <Label htmlFor="morning_comments">Comentários sobre as atividades da manhã</Label>
              <Textarea
                id="morning_comments"
                data-testid="morning-comments-textarea"
                value={formData.morning_comments}
                onChange={(e) => setFormData({...formData, morning_comments: e.target.value})}
              />
            </div>
          </div>

          {/* Seção 3: Atividades da Tarde */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">
              3. Atividades da Tarde
            </h3>
            
            <div>
              <Label>Indique as atividades da TARDE</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                {activityOptions.map(option => (
                  <div key={`afternoon_${option.value}`} className="flex items-center space-x-2">
                    <Checkbox
                      id={`afternoon_${option.value}`}
                      data-testid={`afternoon-activity-${option.value}`}
                      checked={formData.afternoon_activities.includes(option.value)}
                      onCheckedChange={(checked) => handleActivityChange('afternoon_activities', option.value, checked)}
                    />
                    <Label htmlFor={`afternoon_${option.value}`} className="text-sm">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <Label htmlFor="afternoon_comments">Comentários sobre as atividades da tarde</Label>
              <Textarea
                id="afternoon_comments"
                data-testid="afternoon-comments-textarea"
                value={formData.afternoon_comments}
                onChange={(e) => setFormData({...formData, afternoon_comments: e.target.value})}
              />
            </div>
          </div>

          {/* Seção 4: Impactos & Impedimentos */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">
              4. Impactos & Impedimentos
            </h3>
            
            <div>
              <Label>Houveram IMPEDIMENTOS que impactaram na execução das atividades hoje?</Label>
              <RadioGroup 
                value={formData.impediments_occurred?.toString() || ""} 
                onValueChange={(value) => setFormData({...formData, impediments_occurred: value === "true"})}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="impediments_yes" data-testid="impediments-yes-radio" />
                  <Label htmlFor="impediments_yes">Sim</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="impediments_no" data-testid="impediments-no-radio" />
                  <Label htmlFor="impediments_no">Não</Label>
                </div>
              </RadioGroup>
            </div>

            {formData.impediments_occurred && (
              <>
                <div>
                  <Label>Aponte os IMPEDIMENTOS ocorridos durante os trabalhos hoje:</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                    {impedimentOptions.map(option => (
                      <div key={`impediment_${option.value}`} className="flex items-center space-x-2">
                        <Checkbox
                          id={`impediment_${option.value}`}
                          data-testid={`impediment-${option.value}`}
                          checked={formData.impediments_list.includes(option.value)}
                          onCheckedChange={(checked) => handleActivityChange('impediments_list', option.value, checked)}
                        />
                        <Label htmlFor={`impediment_${option.value}`} className="text-sm">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="impediments_comments">Comente sobre os impedimentos listados acima</Label>
                  <Textarea
                    id="impediments_comments"
                    data-testid="impediments-comments-textarea"
                    value={formData.impediments_comments}
                    onChange={(e) => setFormData({...formData, impediments_comments: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="impediment_resolution_time">Tempo de solução do impedimento (HH:MM)</Label>
                  <Input
                    id="impediment_resolution_time"
                    type="time"
                    data-testid="impediment-resolution-time-input"
                    value={formData.impediment_resolution_time}
                    onChange={(e) => setFormData({...formData, impediment_resolution_time: e.target.value})}
                  />
                </div>
              </>
            )}
          </div>

          {/* Seção 5: Administração de Almoxarifado & Movimentação de Spare Parts */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">
              5. Administração de Almoxarifado & Movimentação de Spare Parts
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="region_worked">Qual a região (CAV) trabalhada hoje?</Label>
                <Input
                  id="region_worked"
                  data-testid="region-worked-input"
                  placeholder="Ex: CAV-01"
                  value={formData.region_worked}
                  onChange={(e) => setFormData({...formData, region_worked: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="columns_sandblasted_entirely">Quantas COLUNAS foram jateadas por INTEIRO?</Label>
                <Input
                  id="columns_sandblasted_entirely"
                  type="number"
                  data-testid="columns-sandblasted-entirely-input"
                  value={formData.columns_sandblasted_entirely}
                  onChange={(e) => setFormData({...formData, columns_sandblasted_entirely: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="sandblasted_columns_width">Largura das colunas jateadas (Metros)</Label>
                <Input
                  id="sandblasted_columns_width"
                  type="number"
                  step="0.01"
                  data-testid="sandblasted-columns-width-input"
                  value={formData.sandblasted_columns_width}
                  onChange={(e) => setFormData({...formData, sandblasted_columns_width: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="sandblasted_columns_height">Altura das colunas jateadas (Metros)</Label>
                <Input
                  id="sandblasted_columns_height"
                  type="number"
                  step="0.01"
                  data-testid="sandblasted-columns-height-input"
                  value={formData.sandblasted_columns_height}
                  onChange={(e) => setFormData({...formData, sandblasted_columns_height: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Houve COLUNAS jateadas PARCIALMENTE hoje?</Label>
                <RadioGroup 
                  value={formData.partial_columns_sandblasted?.toString() || ""} 
                  onValueChange={(value) => setFormData({...formData, partial_columns_sandblasted: value === "true"})}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="true" id="partial_sandblasted_yes" data-testid="partial-sandblasted-yes-radio" />
                    <Label htmlFor="partial_sandblasted_yes">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="false" id="partial_sandblasted_no" data-testid="partial-sandblasted-no-radio" />
                    <Label htmlFor="partial_sandblasted_no">Não</Label>
                  </div>
                </RadioGroup>
              </div>
              
              {formData.partial_columns_sandblasted && (
                <div>
                  <Label htmlFor="partial_sandblasted_height">Altura das colunas jateadas PARCIALMENTE (Metros)</Label>
                  <Input
                    id="partial_sandblasted_height"
                    type="number"
                    step="0.01"
                    data-testid="partial-sandblasted-height-input"
                    value={formData.partial_sandblasted_height}
                    onChange={(e) => setFormData({...formData, partial_sandblasted_height: e.target.value})}
                  />
                </div>
              )}
              
              <div>
                <Label htmlFor="sandblasting_hours">Horas de execução de jateamento hoje</Label>
                <Input
                  id="sandblasting_hours"
                  type="number"
                  step="0.5"
                  data-testid="sandblasting-hours-input"
                  value={formData.sandblasting_hours}
                  onChange={(e) => setFormData({...formData, sandblasting_hours: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Houve REPASSAGEM DO JATEAMENTO?</Label>
                <RadioGroup 
                  value={formData.sandblasting_rework?.toString() || ""} 
                  onValueChange={(value) => setFormData({...formData, sandblasting_rework: value === "true"})}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="true" id="sandblasting_rework_yes" data-testid="sandblasting-rework-yes-radio" />
                    <Label htmlFor="sandblasting_rework_yes">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="false" id="sandblasting_rework_no" data-testid="sandblasting-rework-no-radio" />
                    <Label htmlFor="sandblasting_rework_no">Não</Label>
                  </div>
                </RadioGroup>
              </div>
              
              {formData.sandblasting_rework && (
                <div className="md:col-span-2">
                  <Label htmlFor="sandblasting_rework_details">Identifique quais CAVs e COLUNAS</Label>
                  <Textarea
                    id="sandblasting_rework_details"
                    data-testid="sandblasting-rework-details-textarea"
                    value={formData.sandblasting_rework_details}
                    onChange={(e) => setFormData({...formData, sandblasting_rework_details: e.target.value})}
                  />
                </div>
              )}
              
              <div>
                <Label htmlFor="robot_advance_speed">Velocidade de avanço do robô</Label>
                <Input
                  id="robot_advance_speed"
                  type="number"
                  step="0.01"
                  data-testid="robot-advance-speed-input"
                  value={formData.robot_advance_speed}
                  onChange={(e) => setFormData({...formData, robot_advance_speed: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="turbine_speed">Velocidade de funcionamento das turbinas</Label>
                <Input
                  id="turbine_speed"
                  type="number"
                  step="0.01"
                  data-testid="turbine-speed-input"
                  value={formData.turbine_speed}
                  onChange={(e) => setFormData({...formData, turbine_speed: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="turbine_speed_change_cav">CAV com mudança na velocidade</Label>
                <Input
                  id="turbine_speed_change_cav"
                  data-testid="turbine-speed-change-cav-input"
                  value={formData.turbine_speed_change_cav}
                  onChange={(e) => setFormData({...formData, turbine_speed_change_cav: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="sandblasting_current">Corrente utilizada para jateamento</Label>
                <Input
                  id="sandblasting_current"
                  type="number"
                  step="0.01"
                  data-testid="sandblasting-current-input"
                  value={formData.sandblasting_current}
                  onChange={(e) => setFormData({...formData, sandblasting_current: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Houve TROCA DE IMPLEMENTO hoje?</Label>
                <RadioGroup 
                  value={formData.implement_change?.toString() || ""} 
                  onValueChange={(value) => setFormData({...formData, implement_change: value === "true"})}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="true" id="implement_change_yes" data-testid="implement-change-yes-radio" />
                    <Label htmlFor="implement_change_yes">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="false" id="implement_change_no" data-testid="implement-change-no-radio" />
                    <Label htmlFor="implement_change_no">Não</Label>
                  </div>
                </RadioGroup>
              </div>
              
              {formData.implement_change && (
                <div>
                  <Label htmlFor="tool_change_time">Tempo de troca de implemento</Label>
                  <Input
                    id="tool_change_time"
                    type="time"
                    data-testid="tool-change-time-input"
                    value={formData.tool_change_time}
                    onChange={(e) => setFormData({...formData, tool_change_time: e.target.value})}
                  />
                </div>
              )}
            </div>

            {/* Seção de Pintura */}
            <h4 className="text-md font-semibold text-slate-700 mt-6">Dados de Pintura</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="columns_painted_entirely">Quantas colunas foram pintadas por inteiro?</Label>
                <Input
                  id="columns_painted_entirely"
                  type="number"
                  data-testid="columns-painted-entirely-input"
                  value={formData.columns_painted_entirely}
                  onChange={(e) => setFormData({...formData, columns_painted_entirely: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="painted_columns_width">Largura das colunas pintadas (Metros)</Label>
                <Input
                  id="painted_columns_width"
                  type="number"
                  step="0.01"
                  data-testid="painted-columns-width-input"
                  value={formData.painted_columns_width}
                  onChange={(e) => setFormData({...formData, painted_columns_width: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="painted_columns_height">Altura das colunas pintadas (Metros)</Label>
                <Input
                  id="painted_columns_height"
                  type="number"
                  step="0.01"
                  data-testid="painted-columns-height-input"
                  value={formData.painted_columns_height}
                  onChange={(e) => setFormData({...formData, painted_columns_height: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Houve colunas pintadas PARCIALMENTE?</Label>
                <RadioGroup 
                  value={formData.partial_columns_painted?.toString() || ""} 
                  onValueChange={(value) => setFormData({...formData, partial_columns_painted: value === "true"})}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="true" id="partial_painted_yes" data-testid="partial-painted-yes-radio" />
                    <Label htmlFor="partial_painted_yes">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="false" id="partial_painted_no" data-testid="partial-painted-no-radio" />
                    <Label htmlFor="partial_painted_no">Não</Label>
                  </div>
                </RadioGroup>
              </div>
              
              {formData.partial_columns_painted && (
                <>
                  <div>
                    <Label htmlFor="partial_painted_width">Largura das colunas pintadas PARCIALMENTE (Metros)</Label>
                    <Input
                      id="partial_painted_width"
                      type="number"
                      step="0.01"
                      data-testid="partial-painted-width-input"
                      value={formData.partial_painted_width}
                      onChange={(e) => setFormData({...formData, partial_painted_width: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="partial_painted_height">Altura das colunas pintadas PARCIALMENTE (Metros)</Label>
                    <Input
                      id="partial_painted_height"
                      type="number"
                      step="0.01"
                      data-testid="partial-painted-height-input"
                      value={formData.partial_painted_height}
                      onChange={(e) => setFormData({...formData, partial_painted_height: e.target.value})}
                    />
                  </div>
                </>
              )}
              
              <div>
                <Label htmlFor="painting_hours">Horas de execução de pintura hoje</Label>
                <Input
                  id="painting_hours"
                  type="number"
                  step="0.5"
                  data-testid="painting-hours-input"
                  value={formData.painting_hours}
                  onChange={(e) => setFormData({...formData, painting_hours: e.target.value})}
                />
              </div>
            </div>

            {/* Seção de Materiais */}
            <h4 className="text-md font-semibold text-slate-700 mt-6">Controle de Materiais</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="grit_used_volume">Volume de granalha utilizada</Label>
                <Input
                  id="grit_used_volume"
                  type="number"
                  step="0.01"
                  data-testid="grit-used-volume-input"
                  placeholder="Ton/Kg"
                  value={formData.grit_used_volume}
                  onChange={(e) => setFormData({...formData, grit_used_volume: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="grit_remaining_volume">Volume de granalha restante</Label>
                <Input
                  id="grit_remaining_volume"
                  type="number"
                  step="0.01"
                  data-testid="grit-remaining-volume-input"
                  placeholder="Ton/Kg"
                  value={formData.grit_remaining_volume}
                  onChange={(e) => setFormData({...formData, grit_remaining_volume: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="paint_used_volume">Volume de tinta utilizada</Label>
                <Input
                  id="paint_used_volume"
                  type="number"
                  step="0.01"
                  data-testid="paint-used-volume-input"
                  placeholder="Litros"
                  value={formData.paint_used_volume}
                  onChange={(e) => setFormData({...formData, paint_used_volume: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="paint_remaining_volume">Volume de tinta restante</Label>
                <Input
                  id="paint_remaining_volume"
                  type="number"
                  step="0.01"
                  data-testid="paint-remaining-volume-input"
                  placeholder="Litros"
                  value={formData.paint_remaining_volume}
                  onChange={(e) => setFormData({...formData, paint_remaining_volume: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="component_ab_used_volume">Volume de componente A e B utilizado</Label>
                <Input
                  id="component_ab_used_volume"
                  type="number"
                  step="0.01"
                  data-testid="component-ab-used-volume-input"
                  placeholder="Litros"
                  value={formData.component_ab_used_volume}
                  onChange={(e) => setFormData({...formData, component_ab_used_volume: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="component_ab_remaining_volume">Volume de componente A e B restante</Label>
                <Input
                  id="component_ab_remaining_volume"
                  type="number"
                  step="0.01"
                  data-testid="component-ab-remaining-volume-input"
                  placeholder="Litros"
                  value={formData.component_ab_remaining_volume}
                  onChange={(e) => setFormData({...formData, component_ab_remaining_volume: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="inhibitor_used_volume">Volume de inibidor utilizado</Label>
                <Input
                  id="inhibitor_used_volume"
                  type="number"
                  step="0.01"
                  data-testid="inhibitor-used-volume-input"
                  placeholder="Litros"
                  value={formData.inhibitor_used_volume}
                  onChange={(e) => setFormData({...formData, inhibitor_used_volume: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="inhibitor_remaining_volume">Volume de inibidor restante</Label>
                <Input
                  id="inhibitor_remaining_volume"
                  type="number"
                  step="0.01"
                  data-testid="inhibitor-remaining-volume-input"
                  placeholder="Litros"
                  value={formData.inhibitor_remaining_volume}
                  onChange={(e) => setFormData({...formData, inhibitor_remaining_volume: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="solvent_used_volume">Volume de solvente utilizado</Label>
                <Input
                  id="solvent_used_volume"
                  type="number"
                  step="0.01"
                  data-testid="solvent-used-volume-input"
                  placeholder="Litros"
                  value={formData.solvent_used_volume}
                  onChange={(e) => setFormData({...formData, solvent_used_volume: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="solvent_remaining_volume">Volume de solvente restante</Label>
                <Input
                  id="solvent_remaining_volume"
                  type="number"
                  step="0.01"
                  data-testid="solvent-remaining-volume-input"
                  placeholder="Litros"
                  value={formData.solvent_remaining_volume}
                  onChange={(e) => setFormData({...formData, solvent_remaining_volume: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Seção 6: Encerramento do Dia */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">
              6. Encerramento do Dia
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="daily_end_time">Horário de encerramento das atividades</Label>
                <Input
                  id="daily_end_time"
                  type="time"
                  data-testid="daily-end-time-input"
                  value={formData.daily_end_time}
                  onChange={(e) => setFormData({...formData, daily_end_time: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Seção 7: Demandas Administrativas */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">
              7. Demandas Administrativas
            </h3>
            
            <div>
              <Label htmlFor="administrative_demands">Observações e demandas administrativas</Label>
              <Textarea
                id="administrative_demands"
                data-testid="administrative-demands-textarea"
                value={formData.administrative_demands}
                onChange={(e) => setFormData({...formData, administrative_demands: e.target.value})}
                placeholder="Descreva quaisquer demandas administrativas ou observações importantes..."
              />
            </div>
          </div>
          
          <Button type="submit" className="w-full" data-testid="submit-report-btn">
            {editingReport ? "Atualizar Relatório" : "Criar Relatório"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Componente para visualizar relatório COMPLETO
const ReportViewer = ({ report }) => {
  const activityLabels = {
    "RDO_ISSUANCE": "Emissão do Diário de Bordo - Relatório RDO",
    "CORRECTIVE_MAINTENANCE": "Manutenção Corretiva",
    "PREVENTIVE_MAINTENANCE": "Manutenção Preventiva",
    "CONTAINER_ORG_CLEANING": "Organização e Limpeza dos Containers",
    "WAREHOUSE_COUNT": "Contagem do Almoxarifado no Container",
    "SANDBLASTING": "Jateamento",
    "SANDBLASTING_REWORK": "Repassagem de Jateamento",
    "ROUGHNESS_MEASUREMENT": "Medição de Rugosidade",
    "CORROSION_INHIBITOR_APP": "Aplicação de Inibidor de Corrozão",
    "PAINTING_LAYER1": "Pintura - Camada 01",
    "PAINTING_LAYER2": "Pintura - Camada 02",
    "PAINTING_LAYER3": "Pintura - Camada 03",
    "THICKNESS_MEASUREMENT": "Medição de Espessura",
    "OTHER": "Outras"
  };

  const impedimentLabels = {
    "PT_ISSUANCE": "Emissão de PT",
    "PT_APPROVAL": "Aprovação PT",
    "GANGWAY": "Desconexão da Gangway",
    "LOAD_MOVEMENT": "Movimentação de Carga",
    "SWELL_INCREASE": "Aumento nas Ondulações (Swell)",
    "TOOL_CHANGE_BLAST_PAINT": "Troca de implemento de Jato para pintura",
    "SCAFFOLDING_TEAM": "Atendimento da equipe de andâimes",
    "WEATHER_CONDITIONS": "Condições do tempo (Chuva/ vento)",
    "MAINTENANCE": "Manutenções",
    "OTHER": "Outras"
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return 'Não informado';
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : 'Nenhum selecionado';
    return value.toString();
  };

  const renderImage = (imageField, label) => {
    if (report[imageField]) {
      return (
        <div className="mt-2">
          <Label className="font-medium">{label}:</Label>
          <div className="border rounded p-2 bg-gray-50">
            <p className="text-sm text-green-600">📎 {report[imageField]}</p>
            <p className="text-xs text-gray-500">Arquivo anexado</p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6 print:space-y-4">
      {/* Cabeçalho do Relatório */}
      <div className="border-b pb-4">
        <h2 className="text-xl font-bold text-center text-slate-800">RELATÓRIO DIÁRIO DE OPERAÇÕES (RDO)</h2>
        <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
          <div><strong>Data do Relatório:</strong> {formatValue(report.report_date)}</div>
          <div><strong>Versão:</strong> {report.version || 1}</div>
          <div><strong>Status:</strong> {report.submitted ? '✅ Submetido' : '⏳ Rascunho'}</div>
          <div><strong>Criado em:</strong> {new Date(report.created_at).toLocaleString('pt-BR')}</div>
          {report.submitted_at && (
            <div><strong>Submetido em:</strong> {new Date(report.submitted_at).toLocaleString('pt-BR')}</div>
          )}
        </div>
      </div>

      {/* Seção 1: Informações Administrativas */}
      <div className="border rounded p-4">
        <h3 className="text-lg font-semibold text-slate-800 mb-3 border-b pb-2">📋 1. Informações Administrativas</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><strong>Horário na sala de PT:</strong> {formatValue(report.pt_room_time)}</div>
          <div><strong>Horário de emissão da PT:</strong> {formatValue(report.pt_issuance_time)}</div>
          <div><strong>Número das PTs:</strong> {formatValue(report.pt_numbers)}</div>
          <div><strong>Horário de aprovação da PT:</strong> {formatValue(report.pt_approval_time)}</div>
          <div><strong>Início das atividades da manhã:</strong> {formatValue(report.morning_start_time)}</div>
        </div>
      </div>

      {/* Seção 2: Atividades da Manhã */}
      <div className="border rounded p-4">
        <h3 className="text-lg font-semibold text-slate-800 mb-3 border-b pb-2">🌅 2. Atividades da Manhã</h3>
        <div className="text-sm">
          <div className="mb-3">
            <strong>Atividades selecionadas:</strong>
            {report.morning_activities?.length > 0 ? (
              <ul className="list-disc list-inside space-y-1 mt-2">
                {report.morning_activities.map((activity) => (
                  <li key={`morning-${activity}`}>{activityLabels[activity] || activity}</li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-600 mt-1">Nenhuma atividade registrada</p>
            )}
          </div>
          
          {report.morning_activities?.includes('OTHER') && (
            <div className="mb-3">
              <strong>Descrição de "Outras":</strong> {formatValue(report.morning_other_description)}
            </div>
          )}
          
          <div>
            <strong>Comentários:</strong> {formatValue(report.morning_comments)}
          </div>
        </div>
      </div>

      {/* Seção 3: Parada de Almoço */}
      <div className="border rounded p-4">
        <h3 className="text-lg font-semibold text-slate-800 mb-3 border-b pb-2">🍽️ 3. Parada de Almoço</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><strong>Horário de SAÍDA para almoço:</strong> {formatValue(report.lunch_departure_time)}</div>
          <div><strong>Horário de VOLTA do almoço:</strong> {formatValue(report.lunch_return_time)}</div>
        </div>
      </div>

      {/* Seção 4: Atividades da Tarde */}
      <div className="border rounded p-4">
        <h3 className="text-lg font-semibold text-slate-800 mb-3 border-b pb-2">🌇 4. Atividades da Tarde</h3>
        <div className="text-sm">
          <div className="mb-3">
            <strong>Atividades selecionadas:</strong>
            {report.afternoon_activities?.length > 0 ? (
              <ul className="list-disc list-inside space-y-1 mt-2">
                {report.afternoon_activities.map((activity) => (
                  <li key={`afternoon-${activity}`}>{activityLabels[activity] || activity}</li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-600 mt-1">Nenhuma atividade registrada</p>
            )}
          </div>
          
          {report.afternoon_activities?.includes('OTHER') && (
            <div className="mb-3">
              <strong>Descrição de "Outras":</strong> {formatValue(report.afternoon_other_description)}
            </div>
          )}
          
          <div>
            <strong>Comentários:</strong> {formatValue(report.afternoon_comments)}
          </div>
        </div>
      </div>

      {/* Seção 5: Fotos e Comentários */}
      <div className="border rounded p-4">
        <h3 className="text-lg font-semibold text-slate-800 mb-3 border-b pb-2">📸 5. Fotos e Comentários</h3>
        <div className="space-y-3">
          {renderImage('progress_photo_1', 'Foto 1 de Progresso')}
          {renderImage('progress_photo_2', 'Foto 2 de Progresso')}
          {renderImage('progress_photo_3', 'Foto 3 de Progresso')}
          {!report.progress_photo_1 && !report.progress_photo_2 && !report.progress_photo_3 && (
            <p className="text-slate-600 text-sm">Nenhuma foto anexada</p>
          )}
        </div>
      </div>

      {/* Seção 6: Dados Específicos das Atividades */}
      <div className="border rounded p-4">
        <h3 className="text-lg font-semibold text-slate-800 mb-3 border-b pb-2">📊 6. Dados Específicos das Atividades</h3>
        <div className="space-y-4 text-sm">
          <div><strong>Região (CAV) trabalhada:</strong> {formatValue(report.region_worked)}</div>
          
          {/* Dados de Jateamento */}
          <div className="bg-blue-50 p-3 rounded">
            <h4 className="font-semibold mb-2">📌 Dados de Jateamento</h4>
            <div className="grid grid-cols-2 gap-3">
              <div><strong>Horas de jateamento hoje:</strong> {formatValue(report.sandblasting_hours_today)}</div>
              <div><strong>Colunas jateadas inteiras:</strong> {formatValue(report.columns_sandblasted_entirely)}</div>
              <div><strong>Largura colunas jateadas (m):</strong> {formatValue(report.sandblasted_columns_width)}</div>
              <div><strong>Altura colunas jateadas (m):</strong> {formatValue(report.sandblasted_columns_height)}</div>
              <div><strong>Colunas parcialmente jateadas:</strong> {formatValue(report.partial_columns_sandblasted)}</div>
              <div><strong>Altura parcial (m):</strong> {formatValue(report.partial_sandblasted_height)}</div>
              <div><strong>Houve repassagem:</strong> {formatValue(report.sandblasting_rework)}</div>
              <div><strong>Detalhes da repassagem:</strong> {formatValue(report.sandblasting_rework_details)}</div>
              <div><strong>Velocidade robô:</strong> {formatValue(report.robot_advance_speed)}</div>
              <div><strong>Velocidade turbinas:</strong> {formatValue(report.turbine_speed)}</div>
              <div><strong>Mudança velocidade turbina:</strong> {formatValue(report.turbine_speed_changed)}</div>
              <div><strong>CAV com mudança:</strong> {formatValue(report.turbine_speed_change_cav)}</div>
              <div><strong>Corrente jateamento (A):</strong> {formatValue(report.sandblasting_current)}</div>
            </div>
          </div>

          {/* Dados de Pintura */}
          <div className="bg-green-50 p-3 rounded">
            <h4 className="font-semibold mb-2">🎨 Dados de Pintura</h4>
            <div className="grid grid-cols-2 gap-3">
              <div><strong>Colunas pintadas inteiras:</strong> {formatValue(report.columns_painted_entirely)}</div>
              <div><strong>Largura colunas pintadas (m):</strong> {formatValue(report.painted_columns_width)}</div>
              <div><strong>Altura colunas pintadas (m):</strong> {formatValue(report.painted_columns_height)}</div>
              <div><strong>Colunas parcialmente pintadas:</strong> {formatValue(report.partial_columns_painted)}</div>
              <div><strong>Largura parcial (m):</strong> {formatValue(report.partial_painted_width)}</div>
              <div><strong>Altura parcial (m):</strong> {formatValue(report.partial_painted_height)}</div>
              <div><strong>Horas de pintura:</strong> {formatValue(report.painting_hours)}</div>
            </div>
          </div>

          {/* Troca de Implemento */}
          <div className="bg-yellow-50 p-3 rounded">
            <h4 className="font-semibold mb-2">🔧 Troca de Implemento</h4>
            <div className="grid grid-cols-2 gap-3">
              <div><strong>Houve troca implemento:</strong> {formatValue(report.implement_change)}</div>
              <div><strong>Tempo troca (min):</strong> {formatValue(report.tool_change_time)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Seção 7: Impedimentos */}
      <div className="border rounded p-4">
        <h3 className="text-lg font-semibold text-slate-800 mb-3 border-b pb-2">⚠️ 7. Impactos & Impedimentos</h3>
        <div className="text-sm space-y-3">
          <div><strong>Houveram impedimentos:</strong> {formatValue(report.impediments_occurred)}</div>
          
          {report.impediments_occurred && (
            <>
              {report.impediments_list?.length > 0 && (
                <div>
                  <strong>Impedimentos identificados:</strong>
                  <ul className="list-disc list-inside space-y-1 mt-2">
                    {report.impediments_list.map((impediment) => (
                      <li key={`impediment-${impediment}`}>{impedimentLabels[impediment] || impediment}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {report.impediments_list?.includes('OTHER') && (
                <div><strong>Descrição de "Outras":</strong> {formatValue(report.impediments_other_description)}</div>
              )}
              
              <div><strong>Comentários sobre impedimentos:</strong> {formatValue(report.impediments_comments)}</div>
              <div><strong>Tempo total de resolução:</strong> {formatValue(report.impediment_resolution_time)}</div>
            </>
          )}
        </div>
      </div>

      {/* Seção 8: Almoxarifado & Spare Parts */}
      <div className="border rounded p-4">
        <h3 className="text-lg font-semibold text-slate-800 mb-3 border-b pb-2">📦 8. Almoxarifado & Spare Parts</h3>
        <div className="space-y-4 text-sm">
          
          {/* Movimento de Almoxarifado */}
          <div>
            <strong>Movimento de almoxarifado:</strong> {formatValue(report.warehouse_item_moved)}
            {report.warehouse_general_comments && (
              <div className="mt-2">
                <strong>Comentários gerais:</strong> {formatValue(report.warehouse_general_comments)}
              </div>
            )}
          </div>

          {/* Kit Turbina */}
          <div className="bg-orange-50 p-3 rounded">
            <h4 className="font-semibold mb-2">⚙️ Kit Turbina</h4>
            <div className="space-y-2">
              <div><strong>Troca de kit turbina:</strong> {formatValue(report.turbine_kit_replaced)}</div>
              {report.turbine_kit_replaced && (
                <>
                  <div><strong>Posição do kit:</strong> {formatValue(report.turbine_kit_position)}</div>
                  <div><strong>Kit balanceado:</strong> {formatValue(report.turbine_kit_balanced)}</div>
                  <div><strong>Justificativa:</strong> {formatValue(report.turbine_kit_justification)}</div>
                  
                  {/* Fotos do Kit */}
                  <div className="mt-3">
                    <strong>Registro Fotográfico:</strong>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {renderImage('kit_photo_removed', 'Kit Retirado')}
                      {renderImage('kit_photo_replaced', 'Kit Trocado')}
                      {renderImage('kit_photo_balanced', 'Kit Balanceado')}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Kit de Vedação */}
          <div className="bg-purple-50 p-3 rounded">
            <h4 className="font-semibold mb-2">🔧 Kit de Vedação</h4>
            <div className="grid grid-cols-2 gap-3">
              <div><strong>Troca de kits vedação:</strong> {formatValue(report.sealing_kit_replaced)}</div>
              <div><strong>Estoque kits vedação:</strong> {formatValue(report.sealing_kits_stock)}</div>
            </div>
          </div>

          {/* Controle de Materiais */}
          <div className="bg-gray-50 p-3 rounded">
            <h4 className="font-semibold mb-2">📊 Controle de Materiais</h4>
            
            {/* Abrasivo */}
            <div className="mb-3">
              <strong>Abrasivo (Granalha):</strong>
              <div className="grid grid-cols-2 gap-2 mt-1 text-xs">
                <div>• Utilizado: {formatValue(report.grit_used_volume)} kg</div>
                <div>• Restante: {formatValue(report.grit_remaining_volume)} kg</div>
                <div>• Houve perda: {formatValue(report.grit_loss)}</div>
                <div>• Volume perdido: {formatValue(report.grit_lost_volume)} kg</div>
                <div>• Estoque mínimo atingido: {formatValue(report.grit_minimum_reached)}</div>
              </div>
              {report.grit_stock_comments && (
                <div className="mt-1"><strong>Comentários:</strong> {formatValue(report.grit_stock_comments)}</div>
              )}
            </div>

            {/* Materiais de Pintura */}
            <div className="mb-3">
              <strong>Materiais de Pintura:</strong>
              <div className="grid grid-cols-2 gap-2 mt-1 text-xs">
                <div>• Tinta utilizada: {formatValue(report.paint_used_volume)} L</div>
                <div>• Tinta restante: {formatValue(report.paint_remaining_volume)} L</div>
                <div>• Comp. A+B utilizado: {formatValue(report.component_ab_used_volume)} L</div>
                <div>• Comp. A+B restante: {formatValue(report.component_ab_remaining_volume)} L</div>
                <div>• Solvente utilizado: {formatValue(report.solvent_used_volume)} L</div>
                <div>• Solvente restante: {formatValue(report.solvent_remaining_volume)} L</div>
              </div>
            </div>

            {/* Inibidor */}
            <div>
              <strong>Inibidor de Corrosão:</strong>
              <div className="grid grid-cols-2 gap-2 mt-1 text-xs">
                <div>• Utilizado: {formatValue(report.inhibitor_used_volume)} L</div>
                <div>• Restante: {formatValue(report.inhibitor_remaining_volume)} L</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Seção 9: Encerramento */}
      <div className="border rounded p-4">
        <h3 className="text-lg font-semibold text-slate-800 mb-3 border-b pb-2">✅ 9. Encerramento</h3>
        <div className="space-y-3 text-sm">
          <div><strong>Horário de encerramento:</strong> {formatValue(report.daily_end_time)}</div>
          <div>
            <strong>Demandas administrativas:</strong>
            <div className="mt-1 p-2 bg-gray-50 rounded text-xs">
              {formatValue(report.administrative_demands)}
            </div>
          </div>
        </div>
      </div>

      {/* Rodapé */}
      <div className="border-t pt-4 text-center text-xs text-gray-500">
        <p>Relatório gerado automaticamente pelo Sistema RDO</p>
        <p>Data/Hora da visualização: {new Date().toLocaleString('pt-BR')}</p>
      </div>
    </div>
  );
};

// Componente para listar relatórios
const ReportsList = ({ reports, onReportUpdated, showEdit }) => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const downloadReportPDF = async (reportId, reportDate) => {
    try {
      const response = await axios.get(`${API}/download/report/${reportId}`, {
        responseType: 'blob',
      });

      // Criar link de download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `RDO_${reportDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Relatório baixado com sucesso!");
    } catch (error) {
      toast.error("Erro ao baixar relatório");
    }
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setShowViewModal(true);
  };

  const handleEditReport = (report) => {
    setSelectedReport(report);
    setShowEditModal(true);
  };

  const handleUpdateReport = async (updatedData) => {
    try {
      await axios.put(`${API}/reports/${selectedReport.id}`, updatedData);
      toast.success("Relatório atualizado com sucesso!");
      setShowEditModal(false);
      setSelectedReport(null);
      onReportUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar relatório");
    }
  };
  
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Relatórios</h2>
      
      {reports.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-slate-600">Nenhum relatório encontrado.</p>
          </CardContent>
        </Card>
      ) : (
        reports.map(report => (
          <Card key={report.id} data-testid={`report-card-${report.id}`}>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Relatório - {report.report_date}</CardTitle>
                <div className="flex gap-2">
                  {showEdit && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleEditReport(report)}
                      data-testid={`edit-report-${report.id}`}
                    >
                      ✏️ Editar
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleViewReport(report)}
                    data-testid={`view-report-${report.id}`}
                  >
                    👁️ Visualizar
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => downloadReportPDF(report.id, report.report_date)}
                    data-testid={`download-report-${report.id}`}
                    title="PDF completo (apresentação)"
                  >
                    📄 PDF Detalhado
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong>Região (CAV):</strong> {report.region_worked || "N/A"}
                </div>
                <div>
                  <strong>Colunas Jateadas:</strong> {report.columns_sandblasted_entirely || 0}
                </div>
                <div>
                  <strong>Colunas Pintadas:</strong> {report.columns_painted_entirely || 0}
                </div>
                <div>
                  <strong>Impedimentos:</strong> {report.impediments_occurred ? "Sim" : "Não"}
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* Modal de Visualização */}
      {showViewModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="view-modal">
          <Card className="w-full max-w-4xl max-h-screen overflow-y-auto m-4">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Visualizar Relatório - {selectedReport.report_date}</CardTitle>
                <Button 
                  variant="outline" 
                  onClick={() => setShowViewModal(false)}
                  data-testid="close-view-modal"
                >
                  ✕ Fechar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ReportViewer report={selectedReport} />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de Edição */}
      {showEditModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="edit-modal">
          <Card className="w-full max-w-6xl max-h-screen overflow-y-auto m-4">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Editar Relatório - {selectedReport.report_date}</CardTitle>
                <Button 
                  variant="outline" 
                  onClick={() => setShowEditModal(false)}
                  data-testid="close-edit-modal"
                >
                  ✕ Fechar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <RDOFormNew 
                editingReport={selectedReport} 
                onReportCreated={() => {
                  setShowEditModal(false);
                  setSelectedReport(null);
                  onReportUpdated();
                }} 
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Componente do Painel Administrativo
const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: '', email: '', full_name: '', password: '',
    role: 'operator', can_access_dashboard: false, assigned_client_ids: [],
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [clientsList, setClientsList] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchAdminStats();
    fetchClientsList();
  }, []);

  const fetchClientsList = async () => {
    try {
      const response = await axios.get(`${API}/parameters`);
      setClientsList(Array.isArray(response.data) ? response.data : []);
    } catch {
      setClientsList([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Erro ao carregar usuários");
    }
  };

  const fetchAdminStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setAdminStats(response.data);
    } catch (error) {
      toast.error("Erro ao carregar estatísticas administrativas");
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, {
        is_active: !currentStatus
      });
      fetchUsers();
      toast.success(`Usuário ${!currentStatus ? 'ativado' : 'desativado'} com sucesso!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar status do usuário");
    }
  };

  const toggleAdminStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, {
        is_admin: !currentStatus
      });
      fetchUsers();
      toast.success(`Privilégios de admin ${!currentStatus ? 'concedidos' : 'removidos'} com sucesso!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar privilégios de admin");
    }
  };

  const deleteUser = async (userId, username) => {
    if (window.confirm(`Tem certeza que deseja excluir o usuário "${username}"? Esta ação não pode ser desfeita e todos os relatórios do usuário serão excluídos também.`)) {
      try {
        await axios.delete(`${API}/admin/users/${userId}`);
        fetchUsers();
        toast.success("Usuário excluído com sucesso!");
      } catch (error) {
        toast.error(error.response?.data?.detail || "Erro ao excluir usuário");
      }
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!createForm.username || !createForm.password || !createForm.email || !createForm.full_name) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    if (createForm.role === 'viewer' && createForm.assigned_client_ids.length === 0) {
      toast.error("Selecione ao menos um cliente para o usuário Viewer.");
      return;
    }
    try {
      setCreatingUser(true);
      const payload = {
        ...createForm,
        is_admin: createForm.role === 'admin',
        // Admin sempre tem dashboard liberado
        can_access_dashboard: createForm.role === 'admin' ? true : createForm.can_access_dashboard,
      };
      await axios.post(`${API}/admin/users`, payload);
      toast.success(`Usuário "${createForm.username}" criado com sucesso!`);
      setShowCreateModal(false);
      setCreateForm({
        username: '', email: '', full_name: '', password: '',
        role: 'operator', can_access_dashboard: false, assigned_client_ids: [],
      });
      fetchUsers();
      fetchAdminStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar usuário");
    } finally {
      setCreatingUser(false);
    }
  };

  const toggleDashboardAccess = async (userId, currentValue) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, {
        can_access_dashboard: !currentValue,
      });
      fetchUsers();
      toast.success(`Acesso ao Dashboard ${!currentValue ? 'liberado' : 'bloqueado'}.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar acesso");
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-slate-800">⚙️ Painel Administrativo</h2>
      
      {/* Estatísticas Administrativas */}
      {adminStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600" data-testid="admin-total-users">
                {adminStats.totals.total_users}
              </div>
              <div className="text-sm text-slate-600">Total de Usuários</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600" data-testid="admin-active-users">
                {adminStats.totals.active_users}
              </div>
              <div className="text-sm text-slate-600">Usuários Ativos</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-600" data-testid="admin-inactive-users">
                {adminStats.totals.inactive_users}
              </div>
              <div className="text-sm text-slate-600">Usuários Inativos</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-purple-600" data-testid="admin-admin-users">
                {adminStats.totals.admin_users}
              </div>
              <div className="text-sm text-slate-600">Administradores</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-orange-600" data-testid="admin-total-reports">
                {adminStats.totals.total_reports}
              </div>
              <div className="text-sm text-slate-600">Total de Relatórios</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Perguntas Customizadas (globais) */}
      <CustomQuestionsManager />

      {/* Lista de Usuários */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>👥 Gerenciamento de Usuários</CardTitle>
          <Button
            size="sm"
            onClick={() => setShowCreateModal(true)}
            data-testid="admin-new-user-btn"
          >
            ➕ Novo Usuário
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Usuário</th>
                  <th className="text-left p-3">Nome</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Role</th>
                  <th className="text-left p-3">Criado em</th>
                  <th className="text-left p-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className="border-b hover:bg-slate-50" data-testid={`user-row-${user.id}`}>
                    <td className="p-3">
                      <div className="font-medium">{user.username}</div>
                    </td>
                    <td className="p-3">{user.full_name}</td>
                    <td className="p-3">{user.email}</td>
                    <td className="p-3">
                      <Badge 
                        variant={user.is_active ? "default" : "destructive"}
                        data-testid={`user-status-${user.id}`}
                      >
                        {user.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1">
                        <Badge 
                          variant={user.is_admin ? "secondary" : user.role === "viewer" ? "outline" : "outline"}
                          className={user.role === "viewer" ? "border-purple-300 text-purple-700" : ""}
                          data-testid={`user-role-${user.id}`}
                        >
                          {user.is_admin || user.role === "admin"
                            ? "⚡ Admin"
                            : user.role === "viewer"
                              ? "👁️ Cliente"
                              : "👤 Operador"}
                        </Badge>
                        {!user.is_admin && (
                          <Badge
                            variant="outline"
                            className={user.can_access_dashboard ? "border-green-400 text-green-700 text-[10px]" : "border-slate-300 text-slate-500 text-[10px]"}
                            data-testid={`user-dashboard-${user.id}`}
                          >
                            {user.can_access_dashboard ? "📊 Dashboard ✓" : "🔒 Dashboard ✕"}
                          </Badge>
                        )}
                        {user.role === "viewer" && (
                          <span className="text-[10px] text-slate-500">
                            {(user.assigned_client_ids || []).length} cliente(s)
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      {new Date(user.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant={user.is_active ? "destructive" : "default"}
                          onClick={() => toggleUserStatus(user.id, user.is_active)}
                          data-testid={`toggle-status-${user.id}`}
                        >
                          {user.is_active ? "🚫 Desativar" : "✅ Ativar"}
                        </Button>
                        
                        {!user.is_admin && (
                          <Button
                            size="sm"
                            variant={user.can_access_dashboard ? "outline" : "secondary"}
                            onClick={() => toggleDashboardAccess(user.id, user.can_access_dashboard)}
                            data-testid={`toggle-dashboard-${user.id}`}
                          >
                            {user.can_access_dashboard ? "🔒 Bloquear Dashboard" : "🔓 Liberar Dashboard"}
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant={user.is_admin ? "outline" : "secondary"}
                          onClick={() => toggleAdminStatus(user.id, user.is_admin)}
                          data-testid={`toggle-admin-${user.id}`}
                        >
                          {user.is_admin ? "👤 Remover Admin" : "⚡ Tornar Admin"}
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteUser(user.id, user.username)}
                          data-testid={`delete-user-${user.id}`}
                        >
                          🗑️ Excluir
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Top Usuários */}
      {adminStats?.charts?.top_users && (
        <Card>
          <CardHeader>
            <CardTitle>🏆 Top Usuários por Relatórios</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {adminStats.charts.top_users.slice(0, 5).map((userStat, index) => (
                <div key={userStat._id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium">{userStat.full_name}</div>
                      <div className="text-sm text-slate-600">@{userStat.username}</div>
                    </div>
                  </div>
                  <Badge variant="secondary">{userStat.report_count} relatórios</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal: Novo Usuário (admin) */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md" data-testid="admin-create-user-modal">
          <DialogHeader>
            <DialogTitle>Criar Novo Usuário</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4 pt-2">
            <div>
              <Label htmlFor="cu-username">Nome de usuário *</Label>
              <Input
                id="cu-username"
                data-testid="cu-username-input"
                value={createForm.username}
                onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="cu-fullname">Nome completo *</Label>
              <Input
                id="cu-fullname"
                data-testid="cu-fullname-input"
                value={createForm.full_name}
                onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="cu-email">Email *</Label>
              <Input
                id="cu-email"
                type="email"
                data-testid="cu-email-input"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="cu-password">Senha *</Label>
              <Input
                id="cu-password"
                type="password"
                data-testid="cu-password-input"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="cu-role">Tipo de usuário *</Label>
              <select
                id="cu-role"
                data-testid="cu-role-select"
                value={createForm.role}
                onChange={(e) => setCreateForm({
                  ...createForm,
                  role: e.target.value,
                  // Admin sempre tem dashboard
                  can_access_dashboard: e.target.value === 'admin' ? true : createForm.can_access_dashboard,
                  // Limpa clientes se sair de viewer
                  assigned_client_ids: e.target.value === 'viewer' ? createForm.assigned_client_ids : [],
                })}
                className="w-full mt-1 border border-slate-200 rounded px-3 py-2 text-sm bg-white"
              >
                <option value="operator">👤 Operador (preenche RDOs)</option>
                <option value="admin">⚡ Administrador</option>
                <option value="viewer">👁️ Cliente (somente leitura — Dashboard)</option>
              </select>
              {createForm.role === 'viewer' && (
                <p className="text-xs text-slate-500 mt-1">
                  Viewer só verá o Dashboard dos clientes selecionados abaixo.
                </p>
              )}
            </div>

            {createForm.role !== 'admin' && (
              <div className="flex items-center space-x-2 bg-slate-50 p-3 rounded-md border border-slate-200">
                <Checkbox
                  id="cu-dashboard"
                  data-testid="cu-dashboard-checkbox"
                  checked={createForm.can_access_dashboard}
                  onCheckedChange={(checked) => setCreateForm({ ...createForm, can_access_dashboard: !!checked })}
                />
                <Label htmlFor="cu-dashboard" className="text-sm cursor-pointer">
                  🔓 Liberar acesso ao Dashboard analítico
                </Label>
              </div>
            )}

            {createForm.role === 'viewer' && (
              <div>
                <Label>Clientes vinculados *</Label>
                <div className="border border-slate-200 rounded-md p-2 mt-1 max-h-40 overflow-y-auto bg-white space-y-1" data-testid="cu-clients-list">
                  {clientsList.length === 0 && (
                    <p className="text-xs text-slate-500 py-2">Nenhum cliente cadastrado. Crie clientes na aba "Parâmetros" primeiro.</p>
                  )}
                  {clientsList.map(c => {
                    const isSelected = createForm.assigned_client_ids.includes(c.id);
                    return (
                      <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-2 py-1 rounded">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          data-testid={`cu-client-cb-${c.id}`}
                          onChange={(e) => {
                            const ids = e.target.checked
                              ? [...createForm.assigned_client_ids, c.id]
                              : createForm.assigned_client_ids.filter(id => id !== c.id);
                            setCreateForm({ ...createForm, assigned_client_ids: ids });
                          }}
                        />
                        <span>{c.client_name || c.name || c.id}</span>
                        {c.status === 'FINALIZADO' && <span className="text-xs text-slate-400">(Finalizado)</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                data-testid="cu-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={creatingUser}
                data-testid="cu-submit-btn"
              >
                {creatingUser ? "Criando..." : "Criar Usuário"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Componente para estatísticas do dashboard
const DashboardStats = () => {
  const [stats, setStats] = useState(null);
  
  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Erro ao carregar estatísticas");
    }
  };

  if (!stats) return <div>Carregando...</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Dashboard</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.total_reports !== undefined && (
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-blue-600" data-testid="total-reports-stat">
                {stats.total_reports}
              </div>
              <div className="text-sm text-slate-600">Total de Relatórios</div>
            </CardContent>
          </Card>
        )}
        
        {stats.total_users !== undefined && (
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-green-600" data-testid="total-users-stat">
                {stats.total_users}
              </div>
              <div className="text-sm text-slate-600">Total de Usuários</div>
            </CardContent>
          </Card>
        )}
        
        {stats.user_reports !== undefined && (
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-purple-600" data-testid="user-reports-stat">
                {stats.user_reports}
              </div>
              <div className="text-sm text-slate-600">Meus Relatórios</div>
            </CardContent>
          </Card>
        )}
      </div>

      {stats.recent_reports && (
        <Card>
          <CardHeader>
            <CardTitle>Relatórios Recentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.recent_reports.map(report => (
                <div key={report.id} className="flex justify-between items-center p-2 border rounded">
                  <span>Relatório - {report.report_date}</span>
                  <span className="text-sm text-slate-600">
                    {new Date(report.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Componente principal da aplicação
const AppContent = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
          <p className="mt-2 text-slate-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return user ? <Dashboard /> : <AuthPage />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<AppContent />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
}

export default App;